/**
 * Canonical OwnYou demographic value schemas — Slice 0 / AT-14 + AT-15.
 *
 * Authoring source-of-truth for the per-key `ValueSchema` map referenced by
 * ADR 0005 §"Canonical demographic value schemas (Q3)". The wallet's
 * runtime contract is the *synced policy artefact* (`disclosure-policy-v3.json
 * :: canonical_demographics`), NOT this module — JSON cannot safely reference
 * a TS module as a runtime dependency (the JSON-can't-reference-TS rule per
 * ADR 0005 Q3). The policy artefact embeds the schemas as inline JSON.
 *
 * G3 (expanded in AT-21) enforces equivalence; CI fails on drift between
 * (a) this module, (b) `disclosure-policy-v3.json`, (c) wallet validator
 * expectations.
 *
 * AT-14 shipped the architecture-proving slice (`gender` only). AT-15 adds
 * the remaining 9 keys in the ADR 0005 initial-authored set (`marital_status`,
 * `age_range`, `income`, `property_type`, `ownership`, `number_of_children`,
 * `number_of_adults`, `location`, `language`). AT-19 authors the 8 follow-up
 * keys (`education`, `occupation`, `length_of_residence`, `life_stage`,
 * `median_home_value`, `monthly_housing_payment`, `number_of_individuals`,
 * `urbanization`).
 *
 * @traces ADR 0005 Q3, AT-14, AT-15
 */
/**
 * Authoring map of canonical demographic value schemas. AT-14 lands the
 * `gender` enum; AT-15 lands the 9 remaining initial-authored keys; AT-19
 * lands the final 8 follow-up keys.
 *
 * Re-exported from `packages/iab-classifier/src/index.ts`.
 */
export const CANONICAL_DEMOGRAPHIC_VALUE_SCHEMAS = {
    // ─── AT-14 ──────────────────────────────────────────────────────────
    gender: {
        kind: 'enum',
        values: ['M', 'F', 'O', 'U'],
        description: 'Self-reported gender, normalised to a 4-value enum (Male/Female/Other/Unknown).',
    },
    // ─── AT-15 (ADR 0005 Q3 initial-authored set) ───────────────────────
    marital_status: {
        kind: 'enum',
        values: ['single', 'married', 'divorced', 'widowed', 'separated', 'domestic_partner', 'unknown'],
        description: 'Self-reported marital status.',
    },
    age_range: {
        kind: 'regex',
        pattern: '^\\d{2}-\\d{2}$|^65\\+$',
        description: 'Age range bucket, e.g. "18-24", "25-34", "65+".',
    },
    income: {
        kind: 'regex',
        pattern: '^\\d+(-\\d+)?$|^\\d+\\+$',
        description: 'Annual household income in USD as a numeric range or open upper bound, e.g. "50000-74999", "200000+".',
    },
    property_type: {
        kind: 'enum',
        values: ['apartment', 'house', 'condo', 'townhouse', 'other'],
        description: 'Type of dwelling.',
    },
    ownership: {
        kind: 'enum',
        values: ['own', 'rent', 'other'],
        description: 'Housing tenure status.',
    },
    number_of_children: {
        kind: 'regex',
        pattern: '^\\d+$|^5\\+$',
        description: 'Count of children in household (0..4 literal, 5+ for five-or-more).',
    },
    number_of_adults: {
        kind: 'regex',
        pattern: '^\\d+$|^5\\+$',
        description: 'Count of adults in household (0..4 literal, 5+ for five-or-more).',
    },
    // location is an `open` ValueSchema by ADR 0005 Q3 design: the cardinality
    // is high (cities, regions) and the "structured-sub-keys" decomposition
    // (city / region / country) is deferred. The `maxLength: 64` cap matches
    // the wallet's pre-existing demographics-string discipline.
    location: {
        kind: 'open',
        maxLength: 64,
        description: 'Free-form location string (city, region, etc.); structured sub-keys deferred per ADR 0005 Q3.',
    },
    language: {
        kind: 'enum',
        values: ['en', 'es', 'fr', 'de', 'zh', 'ja', 'pt', 'it', 'ko', 'ru', 'ar', 'hi'],
        description: 'Primary spoken/written language as ISO-639-1 code.',
    },
    // ─── AT-19 (ADR 0005 Q3 follow-up authored set) ─────────────────────
    // The remaining 8 keys, completing the 18-key canonical demographic
    // vocabulary. Each schema is hand-aligned with
    // `disclosure-policy-v3.json :: canonical_demographics.<key>`; G3
    // alignment (AT-21) enforces equivalence in CI.
    education: {
        kind: 'enum',
        values: [
            'high_school',
            'some_college',
            'associate',
            'bachelor',
            'graduate',
            'postgraduate',
            'other',
            'unknown',
        ],
        description: 'Highest completed education level, normalised to 8 codes (high_school, some_college, associate, bachelor, graduate, postgraduate, other, unknown).',
    },
    // occupation is an `open` ValueSchema by design: the cardinality is high
    // (every job title humans use), and a structured taxonomy (e.g. ISCO/SOC
    // sub-keys) is deferred to a future slice. The `maxLength: 64` cap
    // matches the wallet's pre-existing demographics-string discipline and
    // forces low-leakage labels (no full job descriptions). PR description
    // calls this open-key choice out per ADR 0005 Q3.
    occupation: {
        kind: 'open',
        maxLength: 64,
        description: 'Free-form occupation/job-title string; structured sub-keys (ISCO/SOC) deferred per ADR 0005 Q3.',
    },
    length_of_residence: {
        kind: 'regex',
        pattern: '^\\d+$|^\\d+-\\d+$|^10\\+$',
        description: 'Years at current residence as integer count, range, or open-ended bucket: e.g. "0", "0-2", "3-5", "10+".',
    },
    life_stage: {
        kind: 'enum',
        values: [
            'young_adult',
            'established_adult',
            'parent',
            'empty_nester',
            'retiree',
            'student',
            'other',
            'unknown',
        ],
        description: 'Self-reported life-stage code, normalised to 8 buckets (young_adult, established_adult, parent, empty_nester, retiree, student, other, unknown).',
    },
    median_home_value: {
        kind: 'regex',
        pattern: '^\\d+(-\\d+)?$|^\\d+\\+$',
        description: 'Median home value in USD as a numeric value, range, or open upper bound, e.g. "300000", "200000-499999", "1000000+".',
    },
    monthly_housing_payment: {
        kind: 'regex',
        pattern: '^\\d+(-\\d+)?$|^\\d+\\+$',
        description: 'Monthly housing payment in USD as a numeric value, range, or open upper bound, e.g. "800", "1500-2499", "5000+".',
    },
    number_of_individuals: {
        kind: 'regex',
        pattern: '^\\d+$|^10\\+$',
        description: 'Total household individuals as integer count or "10+" open bucket: e.g. "1", "5", "10+".',
    },
    urbanization: {
        kind: 'enum',
        values: ['urban', 'suburban', 'rural'],
        description: 'Settlement-density classification of residence, normalised to a 3-value enum (urban/suburban/rural).',
    },
};
//# sourceMappingURL=canonical-demographic-values.js.map